import { create } from 'zustand';
import * as satellite from 'satellite.js';

const EARTH_RADIUS = 6371;
const VISUALIZATION_SCALE = 0.0000002;

export type SatelliteType = 'active' | 'debris' | 'starlink' | 'station';

export interface Position {
  lat: number;
  lng: number;
  alt: number;
}

export interface CartesianPosition {
  x: number;
  y: number;
  z: number;
}

export type GeographicPosition = Position;

export interface Satellite {
  id: string;
  name: string;
  type: SatelliteType;
  position: Position;
  tle?: {
    line1: string;
    line2: string;
  };
  orbital?: {
    period: number;
    inclination: number;
    eccentricity: number;
    altitude: number;
    phase: number;
  };
  lastUpdate?: number;
  targetPosition?: Position;
}

export interface TimelineRange {
  start: Date;
  end: Date;
  current: Date;
}

interface SatelliteState {
  satellites: Satellite[];
  selectedSatellite: string | null;
  hoveredSatellite: string | null;
  currentTime: Date;
  lastUpdate: number;
  loading: boolean;
  error: string | null;
  timelineRange: TimelineRange;
  objectLimit: number;
  setSelectedSatellite: (id: string | null) => void;
  setHoveredSatellite: (id: string | null) => void;
  setCurrentTime: (time: Date) => void;
  updateSatellites: (time: Date) => void;
  fetchSatellites: (categories: string[]) => Promise<void>;
  fetchHistoricalData: (satelliteId: string, start: Date, end: Date) => Promise<void>;
  setTimelineCurrent: (time: Date) => void;
  setObjectLimit: (limit: number) => void;
}

const CELESTRAK_URLS: Record<string, string | string[]> = {
  active: '/data/tle/active.txt',
  stations: '/data/tle/stations.txt',
  starlink: '/data/tle/starlink.txt',
  debris: [
    '/data/tle/fengyun-1c-debris.txt',
    '/data/tle/iridium-33-debris.txt',
    '/data/tle/cosmos-2251-debris.txt',
    '/data/tle/cosmos-1408-debris.txt'
  ]
};

function isCartesianPosition(position: Position | CartesianPosition): position is CartesianPosition {
  return 'x' in position && 'y' in position && 'z' in position;
}

function isGeographicPosition(position: Position | CartesianPosition): position is Position {
  return 'lat' in position && 'lng' in position && 'alt' in position;
}

function cartesianToGeographic(position: CartesianPosition): Position {
  const { x, y, z } = position;
  const r = Math.sqrt(x * x + y * y + z * z);
  const lat = Math.asin(z / r) * (180 / Math.PI);
  const lng = Math.atan2(y, x) * (180 / Math.PI);
  const alt = (r / VISUALIZATION_SCALE) - EARTH_RADIUS;
  return { lat, lng, alt };
}

function geographicToCartesian(position: Position): CartesianPosition {
  const { lat, lng, alt } = position;
  const r = (EARTH_RADIUS + alt) * VISUALIZATION_SCALE;
  const latRad = lat * (Math.PI / 180);
  const lngRad = lng * (Math.PI / 180);
  const x = r * Math.cos(latRad) * Math.cos(lngRad);
  const y = r * Math.cos(latRad) * Math.sin(lngRad);
  const z = r * Math.sin(latRad);
  return { x, y, z };
}

function calculateSatellitePosition(sat: Satellite, time: Date): Position {
  if (!sat.tle?.line1 || !sat.tle?.line2) {
    return sat.position;
  }

  const satrec = satellite.twoline2satrec(sat.tle.line1, sat.tle.line2);
  if (!satrec) {
    return sat.position;
  }

  const positionAndVelocity = satellite.propagate(satrec, time);
  if (!positionAndVelocity.position) {
    return sat.position;
  }

  // Convert ECI coordinates to ECEF
  const gmst = satellite.gstime(time);
  const positionEcf = satellite.eciToEcf(positionAndVelocity.position as satellite.EciVec3<number>, gmst);

  // Convert to geographic coordinates
  const geodeticPosition = satellite.eciToGeodetic(positionAndVelocity.position as satellite.EciVec3<number>, gmst);

  return {
    lat: satellite.degreesLat(geodeticPosition.latitude),
    lng: satellite.degreesLong(geodeticPosition.longitude),
    alt: geodeticPosition.height * 1000 // Convert to kilometers
  };
}

function calculateOrbitalPosition(sat: Satellite, time: Date): Position {
  if (!sat.orbital) {
    return sat.position;
  }

  const { altitude, inclination, period, phase } = sat.orbital;
  const elapsedTime = time.getTime() / 1000;
  const orbitalProgress = (elapsedTime % period) / period;
  const currentPhase = (phase + orbitalProgress * 360) % 360;
  const phaseRad = currentPhase * (Math.PI / 180);
  const inclinationRad = inclination * (Math.PI / 180);

  const r = EARTH_RADIUS + altitude;
  const x = r * Math.cos(phaseRad);
  const y = r * Math.sin(phaseRad) * Math.cos(inclinationRad);
  const z = r * Math.sin(phaseRad) * Math.sin(inclinationRad);

  return cartesianToGeographic({
    x: x * VISUALIZATION_SCALE,
    y: y * VISUALIZATION_SCALE,
    z: z * VISUALIZATION_SCALE
  });
}

export const useSatelliteStore = create<SatelliteState>((set, get) => {
  let lastFrameTime = Date.now();
  let accumulatedTime = 0;
  const FIXED_TIME_STEP = 1000 / 60; // 60fps

  return {
    satellites: [],
    selectedSatellite: null,
    hoveredSatellite: null,
    currentTime: new Date(),
    lastUpdate: Date.now(),
    loading: false,
    error: null,
    objectLimit: 250,
    timelineRange: {
      start: new Date(),
      end: new Date(),
      current: new Date()
    },

    setSelectedSatellite: (id: string | null) => set({ selectedSatellite: id }),
    setHoveredSatellite: (id: string | null) => set({ hoveredSatellite: id }),
    setObjectLimit: (limit: number) => set({ objectLimit: limit }),

    setCurrentTime: (time: Date) => {
      const now = Date.now();
      const deltaTime = now - lastFrameTime;
      lastFrameTime = now;

      // Only update if enough time has passed
      if (deltaTime < 16) return; // Cap at ~60fps

      accumulatedTime += deltaTime;
      while (accumulatedTime >= FIXED_TIME_STEP) {
        get().updateSatellites(time);
        accumulatedTime -= FIXED_TIME_STEP;
      }

      set({ currentTime: time });
    },

    updateSatellites: (time: Date) => {
      const { satellites, objectLimit } = get();
      const now = Date.now();
      const deltaTime = (now - get().lastUpdate) / 1000;

      // Only update visible satellites based on object limit
      const visibleSatellites = satellites.slice(0, objectLimit);
      const restSatellites = satellites.slice(objectLimit);

      // Batch updates to reduce state changes
      const updatedSatellites = visibleSatellites.map(sat => {
        try {
          // Skip update if the satellite was recently updated
          if (sat.lastUpdate && now - sat.lastUpdate < 100) {
            return sat;
          }

          const targetPosition = sat.tle ? calculateSatellitePosition(sat, time) :
                               sat.orbital ? calculateOrbitalPosition(sat, time) :
                               sat.position;

          // If this is the first update or we don't have a target position
          if (!sat.targetPosition || !sat.lastUpdate) {
            return {
              ...sat,
              position: targetPosition,
              targetPosition,
              lastUpdate: now
            };
          }

          // Interpolate between current position and target position
          const t = Math.min(deltaTime * 2, 1); // 2 = speed factor
          const newPosition = interpolatePositions(sat.position, targetPosition, t);

          return {
            ...sat,
            position: newPosition,
            targetPosition,
            lastUpdate: now
          };
        } catch (error) {
          console.error(`Error updating position for satellite ${sat.name}:`, error);
          return sat;
        }
      });

      // Only update state if positions have actually changed
      const hasChanges = updatedSatellites.some((sat, i) => 
        sat.position !== visibleSatellites[i].position
      );

      if (hasChanges) {
        set({
          satellites: [...updatedSatellites, ...restSatellites],
          lastUpdate: now
        });
      }
    },

    fetchSatellites: async (categories: string[]) => {
      set({ loading: true, error: null });
      try {
        const satellites: Satellite[] = [];
        let id = 1;

        for (const category of categories) {
          const urlsConfig = CELESTRAK_URLS[category];
          if (!urlsConfig) continue;

          const urls = Array.isArray(urlsConfig) ? urlsConfig : [urlsConfig];

          for (const url of urls) {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch TLE data from ${url}`);
            }
            const text = await response.text();
            const lines = text.split('\n').filter(line => line.trim());

            for (let i = 0; i < lines.length; i += 3) {
              if (i + 2 >= lines.length) break;

              const name = lines[i].trim();
              const line1 = lines[i + 1];
              const line2 = lines[i + 2];

              try {
                const satrec = satellite.twoline2satrec(line1, line2);
                if (!satrec) continue;

                const now = new Date();
                const positionAndVelocity = satellite.propagate(satrec, now);
                if (!positionAndVelocity.position) continue;

                const gmst = satellite.gstime(now);
                const geodeticPosition = satellite.eciToGeodetic(
                  positionAndVelocity.position as satellite.EciVec3<number>,
                  gmst
                );

                const position = {
                  lat: satellite.degreesLat(geodeticPosition.latitude),
                  lng: satellite.degreesLong(geodeticPosition.longitude),
                  alt: geodeticPosition.height // Keep in kilometers
                };

                let type: SatelliteType;
                if (category === 'stations') type = 'station';
                else if (category === 'starlink') type = 'starlink';
                else if (category === 'debris') type = 'debris';
                else type = 'active';

                satellites.push({
                  id: String(id++),
                  name,
                  type,
                  position,
                  tle: {
                    line1,
                    line2
                  }
                });
              } catch (error) {
                console.error(`Error processing TLE data for ${name}:`, error);
              }
            }
          }
        }

        set({ satellites, loading: false });
      } catch (error) {
        console.error('Error fetching satellites:', error);
        set({ 
          loading: false, 
          error: error instanceof Error ? error.message : 'Failed to fetch satellites' 
        });
      }
    },

    fetchHistoricalData: async (satelliteId: string, start: Date, end: Date) => {
      set({ loading: true, error: null });
      try {
        // Implement your historical data fetching logic here
        // You can use the satelliteId to fetch specific satellite data
        set({ 
          timelineRange: {
            start,
            end,
            current: start
          },
          loading: false 
        });
      } catch (error) {
        set({ loading: false, error: error instanceof Error ? error.message : 'Failed to fetch historical data' });
      }
    },

    setTimelineCurrent: (time: Date) => {
      set(state => ({
        timelineRange: {
          ...state.timelineRange,
          current: time
        }
      }));
    }
  };
});

function interpolatePositions(current: Position, target: Position, t: number): Position {
  if (isGeographicPosition(current) && isGeographicPosition(target)) {
    return {
      lat: current.lat + (target.lat - current.lat) * t,
      lng: current.lng + (target.lng - current.lng) * t,
      alt: current.alt + (target.alt - current.alt) * t
    };
  }
  
  // Convert both positions to cartesian, interpolate, then convert back
  const currentCart = isCartesianPosition(current) ? current : geographicToCartesian(current);
  const targetCart = isCartesianPosition(target) ? target : geographicToCartesian(target);
  
  const interpolated = {
    x: currentCart.x + (targetCart.x - currentCart.x) * t,
    y: currentCart.y + (targetCart.y - currentCart.y) * t,
    z: currentCart.z + (targetCart.z - targetCart.z) * t
  };
  
  return cartesianToGeographic(interpolated);
}

export function getGeographicPosition(position: Position | CartesianPosition): Position {
  if (isGeographicPosition(position)) {
    return position;
  }
  return cartesianToGeographic(position);
}

export function getCartesianPosition(position: Position | CartesianPosition): CartesianPosition {
  if (isCartesianPosition(position)) {
    return position;
  }
  return geographicToCartesian(position as Position);
} 